/**
 * 🎓 DIAGNOSTIC WRAPPER - GESTION ONBOARDING PÉDAGOGIQUE
 * ======================================================
 * 
 * Ce composant gère l'affichage conditionnel du diagnostic d'entrée.
 * Il vérifie si l'utilisateur a déjà complété le diagnostic et l'affiche
 * en modal/overlay si nécessaire.
 * 
 * Qualiopi Indicateur 11 - Évaluation initiale des acquis
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X } from 'lucide-react';
import EntryDiagnostic from './EntryDiagnostic';
import { getBackendUrl } from '../config/environment';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/**
 * DiagnosticWrapper - Affiche le diagnostic d'entrée si nécessaire
 * 
 * @param {React.ReactNode} children - Contenu à afficher après le diagnostic
 * @param {boolean} forceShow - Force l'affichage du diagnostic (pour tests)
 */
const DiagnosticWrapper = ({ children, forceShow = false }) => {
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [loading, setLoading] = useState(true);
  const [diagnosticStatus, setDiagnosticStatus] = useState(null);

  useEffect(() => {
    checkDiagnosticStatus();
  }, []);

  const checkDiagnosticStatus = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/diagnostic/entry/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDiagnosticStatus(response.data);
      
      // Afficher le diagnostic si pas encore complété
      if (!response.data.has_entry_diagnostic || forceShow) {
        setShowDiagnostic(true);
      }
    } catch (err) {
      console.error('Erreur vérification diagnostic:', err);
      // En cas d'erreur, on ne bloque pas l'utilisateur
      setShowDiagnostic(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDiagnosticComplete = (result) => {
    console.log('✅ Diagnostic complété:', result);
    setShowDiagnostic(false);
    setDiagnosticStatus({
      has_entry_diagnostic: true,
      entry_score: result.percentage
    });
  };

  const handleSkip = () => {
    console.log('⏭️ Diagnostic passé');
    setShowDiagnostic(false);
  };

  // Pendant le chargement, afficher le contenu normalement
  if (loading) {
    return <>{children}</>;
  }

  return (
    <>
      {/* Contenu principal */}
      {children}

      {/* Modal Diagnostic d'entrée */}
      <AnimatePresence>
        {showDiagnostic && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={handleSkip}
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              {/* Close Button */}
              <button
                onClick={handleSkip}
                className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="Fermer"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              {/* Diagnostic Component */}
              <EntryDiagnostic
                onComplete={handleDiagnosticComplete}
                onSkip={handleSkip}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DiagnosticWrapper;
